import clsx from 'clsx';
import { useId, useRef, useState } from 'react';

import FAIcon from '@/componentsv2/Icon/FAIcon';
import Popover from '@/componentsv2/Popover';

const supportEmail = 'hello@onfrontiers.com';

interface EmailDeliveryHelpProps {
  className?: string;
  /** Which edge of the trigger the panel lines up with. Match the trigger's own alignment. */
  align?: 'start' | 'center' | 'end';
  /** Open above the trigger, for a trigger at the bottom of a dialog. */
  above?: boolean;
}

const horizontal = { start: 'left', center: 'center', end: 'right' } as const;

// ENG-5246: what a user can check when a code or verification email does not
// arrive. The api already delivers auth email past SendGrid's suppression
// lists, so what is left is on the recipient's side.
export function EmailDeliveryHelp({
  className,
  align = 'end',
  above = false,
}: EmailDeliveryHelpProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={clsx(
          'inline-flex min-h-48 items-center gap-6 whitespace-nowrap text-brand-primary body-2',
          className
        )}
      >
        <FAIcon iconSet="fal" icon="question-circle" className="text-16" aria-hidden="true" />
        I&apos;m not receiving emails
      </button>
      <Popover
        id={panelId}
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        ariaLabel="Not receiving our emails?"
        anchorOrigin={{ vertical: above ? 'top' : 'bottom', horizontal: horizontal[align] }}
        transformOrigin={{ vertical: above ? 'bottom' : 'top', horizontal: horizontal[align] }}
        className="w-[32rem] max-w-[calc(100vw-3.2rem)]"
      >
        <div className="flex flex-col gap-8 p-16 text-on-surface body-2">
          <span className="subtitle-1">Not receiving our emails?</span>
          <ul className="flex list-disc flex-col gap-4 pl-20 font-normal">
            <li>
              Check your spam, junk, promotions and other folders. Search for
              &quot;OnFrontiers&quot;.
            </li>
            <li>Wait a few minutes, then use Resend.</li>
            <li>
              Still nothing? Contact{' '}
              <a
                className="text-brand-primary no-underline hover:no-underline"
                href={`mailto:${supportEmail}`}
              >
                {supportEmail}
              </a>
              .
            </li>
          </ul>
        </div>
      </Popover>
    </>
  );
}
